import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpEventType, HttpHeaders } from '@angular/common/http';

import { BehaviorSubject, firstValueFrom, Observable, of, Subject} from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AssistantState, AssistantStep, Usage, ClientDiscussion, PromptAttachment, PromptSelection } from './kng-model.assistant';
// For your markdown library import
// import * as MarkdownIt from 'markdown-it';

/**
 * Contenu sélectionné dans un éditeur (Tiptap rules-editor) que l'utilisateur
 * souhaite passer en contexte à Edgar lors de la prochaine requête.
 *
 * - `text` : texte brut sélectionné (servira au prompt + à l'aperçu du tag).
 * - `source` : slug/titre court du document source (affiché dans le tag).
 * - `from` / `to` : positions ProseMirror (utile pour réinjecter un patch).
 */
export interface SelectedContent {
  text: string;
  source?: string;
  from?: number;
  to?: number;
}

/**
 * ✅ Extract steps from a message content (pure parsing)
 */
export function parseSteps(message: string): AssistantStep[] {
  if (!message) return [];
  const jsonraw = message.match(/<step>([\s\S]*?)<\/step>/gm)?.map(step => step.replace(/<\/?step>/g, '')) || [];
  return jsonraw.map(step => {
    try {
      const json = JSON.parse(step);
      return { description: json.description || step, ...json };
    } catch (e) {
      return { description: step };
    }
  });
}

/**
 * Texte affichable en console/chat : les tags de comportement UX sont retirés.
 * Les consommateurs qui doivent interpréter ces tags (ex. éditeur rules pour
 * `<patch>`) peuvent demander le stream brut via `stream(..., { raw: true })`.
 */
export function cleanAssistantConsoleText(text: string): string {
  return text
    .replace(/<thinking>[\s\S]*?<\/thinking>/g, '')
    .replace(/<step>[\s\S]*?<\/step>/g, '')
    .replace(/<patch>[\s\S]*?<\/patch>/g, '')
    .replace(/<memories>[\s\S]*?<\/memories>/g, '');
}

/**
 * ✅ Private: Deduplicate steps by replacing duplicates with counter suffix (N)
 * - Keeps only one entry per base description
 * - Updates counter on each occurrence
 * - Can be removed or improved without affecting parseSteps
 */
function _deduplicateSteps(steps: AssistantStep[]): AssistantStep[] {
  const countMap = new Map<string, number>();
  const result: AssistantStep[] = [];

  for (const step of steps) {
    //
    // Extract base description (remove existing counter if present)
    const baseDesc = step.description.replace(/\s*\(\d+\)$/, '');

    //
    // Increment count for this base description
    const count = (countMap.get(baseDesc) || 0) + 1;
    countMap.set(baseDesc, count);

    //
    // Find existing step with same base description
    const existingIndex = result.findIndex(s =>
      s.description.replace(/\s*\(\d+\)$/, '') === baseDesc
    );

    //
    // Create step with counter suffix if count > 1
    const newStep = {
      ...step,
      description: count > 1 ? `${baseDesc} (${count})` : baseDesc
    };

    //
    // Replace existing or add new
    if (existingIndex >= 0) {
      result[existingIndex] = newStep;
    } else {
      result.push(newStep);
    }
  }

  return result;
}

@Injectable({
  providedIn: 'root'
})
export class KngAssistantAiService {
  private defaultUsage: Usage = {prompt: 0, completion: 0, cost: 0, total: 0};
  private defaultState: AssistantState = { status: 'init', usage: this.defaultUsage};

  // ✅ StateGraph-only BehaviorSubject
  private discussionSubject = new BehaviorSubject<ClientDiscussion>({
    id: null,
    messages: [],
    usage: this.defaultUsage,
    createdAt: new Date(),
    updatedAt: new Date()
  });

  private stateSubject = new BehaviorSubject<AssistantState>(this.defaultState);
  private transcriptionsSubject = new BehaviorSubject<string>('');
  private memoriesSubject = new BehaviorSubject<any[]>([]);  // ✅ NEW: Memories state

  // ✅ NEW: Event system for prompt cross-component communication
  private promptEventSubject = new Subject<string>();

  //
  // Legacy fallback for raw context injection.
  // Prefer PromptAttachment[] for new integrations.
  private promptContextSubject = new BehaviorSubject<string | null>(null);
  private promptAttachmentsSubject = new BehaviorSubject<PromptAttachment[]>([]);
  private promptSelectionSubject = new BehaviorSubject<PromptSelection | null>(null);
  private selectedContentSubject = new BehaviorSubject<SelectedContent | null>(null);

  // ✅ StateGraph-only observables
  discussion$ = this.discussionSubject.asObservable();
  state$ = this.stateSubject.asObservable();
  transcriptions$ = this.transcriptionsSubject.asObservable();
  memories$ = this.memoriesSubject.asObservable();  // ✅ NEW: Observable for memories
  promptEvt$ = this.promptEventSubject.asObservable();
  promptContext$ = this.promptContextSubject.asObservable();
  promptAttachments$ = this.promptAttachmentsSubject.asObservable();
  promptSelection$ = this.promptSelectionSubject.asObservable();
  selectedContent$ = this.selectedContentSubject.asObservable();

  //
  // Ordre: top candidats (fréquence/impact) en premier
  //
  questions = {
    'Contentieux': [
      {
        "question": "Donne-moi la situation locataire (contentieux) pour la référence 2015.042 021.14.",
        "note": "Situation locataire / soldes depuis refLocataire"
      },
      {
        "question": "Constitue le dossier contentieux du locataire 2015.042 021.14",
        "note": "Profil + soldes + documents liés"
      },
      {
        "question": "Peux-tu me donner les honoraires travaux validés pour la relocation de 1459.044 001.22 ?",
        "note": "resolveValidationNouveauLoyer par référence locataire"
      },

    ],
    'Procédures': [
      {
        "question": "Comment traiter une résiliation de bail pour un locataire ?",
        "note": "Procédure contentieux via lookupKnowledge"
      },
      {
        "question": "Quelle est la procédure pour les 4ème rappels et plus (analyses contentieux) ?",
        "note": "Procédure analyses contentieux"
      },
      {
        "question": "Comment traiter les poursuites pour un locataire sorti ?",
        "note": "Procédure poursuites locataires sortis"
      },
      {
        "question": "Comment gérer un arrangement de paiement avec un locataire ?",
        "note": "Procédure arrangements de paiement"
      },
      {
        "question": "Que faire d'un recommandé non réclamé ?",
        "note": "Procédure recommandés non réclamés"
      },
      {
        "question": "Comment traiter les loyers irrécouvrables et gratuités de loyer ?",
        "note": "Procédure loyers irrécouvrables"
      },
    ],
    'Honoraires': [
      {
        "question": "Quelle est la procédure pour traiter une note d'honoraire (NH) ?",
        "note": "Procédure NH via lookupKnowledge"
      },
      {
        "question": "Comment sont traitées les notes d'honoraires aux propriétaires ?",
        "note": "Procédure NH propriétaires"
      },
    ],
  }

  sgc = [
    {
      category: "S1",
      question: "Obtiens la situation locataire pour la référence locataire 2015.042 021.14.",
      note: "Situation locataire / contentieux depuis refLocataire"
    }
  ];

  constructor(private $http: HttpClient) {}

  // ✅ StateGraph-only getters
  get discussion(): ClientDiscussion {
    return this.discussionSubject.value;
  }

  get discussionMessages(): Array<{id: string, role: 'user' | 'assistant', content: string, timestamp: Date}> {
    return this.discussion.messages;
  }

  get state(): AssistantState {
    return this.stateSubject.value;
  }

  get queries() {
    return this.questions;
  }

  private cleanText(text:string) {
    return cleanAssistantConsoleText(text);
  }

  updateState(partial: Partial<AssistantState>) {
    this.stateSubject.next({
      ...this.state,
      ...partial
    });
  }


  // ✅ NEW: Cross-component prompt communication + chat trigger
  sendPrompt(prompt: string, agent?: string, ragname?: string, rules?: string[], attachments?: PromptAttachment[], selection?: PromptSelection | null) {
    this.promptEventSubject.next(prompt);
    return this.chat(prompt, {runAgent: agent, ragname, rules, attachments, selection});
  }

  /**
   * Définit le contexte actif (ex: emails sélectionnés, document, etc.)
   * - text: contenu brut injecté dans le query sous forme de tag <context>
   * - null: aucun contexte actif
   */
  setPromptContext(text: string | null): void {
    this.promptContextSubject.next(text);
  }

  /** Efface le contexte après envoi du prompt */
  clearPromptContext(): void {
    this.promptContextSubject.next(null);
  }

  setPromptAttachments(attachments: PromptAttachment[]): void {
    this.promptAttachmentsSubject.next(attachments);
  }

  /**
   * Ajoute un attachment à la sélection courante (dédupliqué par type+data).
   */
  addPromptAttachment(attachment: PromptAttachment): void {
    const current = this.promptAttachmentsSubject.value;
    const exists = current.some(a => a.type === attachment.type && a.data === attachment.data);
    if (exists) {
      return;
    }
    this.promptAttachmentsSubject.next([...current, attachment]);
  }

  /**
   * Retire un attachment de la sélection courante.
   */
  removePromptAttachment(type: PromptAttachment['type'], data: string): void {
    const current = this.promptAttachmentsSubject.value;
    this.promptAttachmentsSubject.next(
      current.filter(a => !(a.type === type && a.data === data))
    );
  }

  clearPromptAttachments(): void {
    this.promptAttachmentsSubject.next([]);
  }

  /**
   * Upload un PDF comme asset réutilisable pour le chat (ctrlUploadAsset).
   * Aucun document/règle n'est créé : le serveur met le fichier en cache (source='file')
   * et renvoie { slug, href } référençable dans le prompt via un attachment {type:'file'}.
   */
  uploadAsset(file: File): Promise<{ slug: string; href: string; title: string; contentType: string }> {
    const formData = new FormData();
    formData.append('document', file, file.name);

    return firstValueFrom(
      this.$http.post<{ slug: string; href: string; title: string; contentType: string }>(
        '/api/agent/upload/asset',
        formData,
        { withCredentials: true }
      )
    );
  }

  get promptSelection(): PromptSelection | null {
    return this.promptSelectionSubject.value;
  }

  setPromptSelection(selection: PromptSelection | null): void {
    if (!selection || !selection.text?.trim()) {
      this.promptSelectionSubject.next(null);
      return;
    }

    this.promptSelectionSubject.next({
      ...selection,
      text: selection.text.trim()
    });
  }

  clearPromptSelection(): void {
    this.promptSelectionSubject.next(null);
  }

  /**
   * Retourne le contenu sélectionné actif (ou null).
   * Pattern miroir de `KngMemoriesService.selectedMemoryId`.
   */
  get selectedContent(): SelectedContent | null {
    return this.selectedContentSubject.value;
  }

  /**
   * Définit le contenu sélectionné actif (typiquement depuis le popup Tiptap
   * via le bouton "Edgar"). Les composants `kng-prompt` / `kng-prompt-mini`
   * écoutent `selectedContent$` pour afficher un tag visuel removable.
   *
   * Appeler avec `null` pour effacer.
   */
  setSelectedContent(content: SelectedContent | null): void {
    if (!content || !content.text?.trim()) {
      this.selectedContentSubject.next(null);
      return;
    }
    this.selectedContentSubject.next(content);
  }

  /** Efface le contenu sélectionné actif. */
  clearSelectedContent(): void {
    this.selectedContentSubject.next(null);
  }

  emails() {
    const headers = {
      'Content-Type': 'application/json',
      'ngsw-bypass': 'true',
      'Cache-Control': 'no-cache',
    };
    return this.$http.get('/api/emails', {
      withCredentials: true,
      headers
    });
  }

  historyDel(messageId: string, agent?: string) {
    const discussion = this.discussionSubject.value;
    if(!messageId) {
      return of(discussion);
    }

    // ✅ Use new URL structure with agent parameter
    const agentParam = agent || 'current';
    return this.$http.post(`/api/agent/${agentParam}/history/none/${messageId}`, {}, {
      withCredentials: true,
      headers: {
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json',
      }
    }).pipe(
      tap((res: any) => {
        // Update discussion with server response
        this.discussionSubject.next(res);
      }),
      catchError((error) => {
        const message = error.error?.message || 'Erreur lors de la suppression de l\'historique';
        this.notify(message, 'danger', 'exclamation-triangle');
        return of(error);
      })
    );
  }

  memories() {
    return this.$http.get('/api/agent/memories', {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
      }
    }).pipe(
      tap((memories: any) => {
        // ✅ Update memories$ observable
        this.memoriesSubject.next(memories);
      })
    );
  }

  memoryDel(id: string): Observable<any> {
    return this.$http.delete(`/api/agent/memories/${id}`, {
      withCredentials: true,
      headers: {
        'ngsw-bypass': 'true',
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json',
      }
    }).pipe(
      tap(() => {
        // ✅ Update memories$ observable after deletion
        const current = this.memoriesSubject.value;
        this.memoriesSubject.next(current.filter((m: any) => m.id !== id));
        this.notify('Memory deleted successfully', 'success', 'check-circle');
      }),
      catchError((error) => {
        const message = error.error?.message || 'Erreur lors de la suppression de la mémoire';
        this.notify(message, 'danger', 'exclamation-triangle');
        return of(error);
      })
    );
  }

  history(clear?: boolean, agent?: string) {
    // ✅ Use new URL structure with agent parameter
    const agentParam = agent || 'current';
    const params = [];
    if (clear) params.push("clear=true");

    const queryString = params.length > 0 ? "?" + params.join("&") : "";

    const headers = {
      'Content-Type': 'application/json',
      'ngsw-bypass': 'true',
      'Cache-Control': 'no-cache',
    };
    return this.$http.get<ClientDiscussion>(`/api/agent/${agentParam}/history` + queryString, {
      withCredentials: true,
      headers
    }).pipe(
      tap((res: ClientDiscussion) => {
        // Process messages to remove memory content
        if (res.messages && Array.isArray(res.messages)) {
          res.messages = res.messages.map((msg: any) => {
            if (typeof msg.content === 'string') {
              msg.content = msg.content.replace(/<memories [^>]+[\s\S]*?<\/memories>/g, '');
            }
            return msg;
          });
        }

        // console.log('🐛 DBG processed messages:', res.messages?.length || 0);

        // Update StateGraph discussion
        this.discussionSubject.next(res);

        // Update state - use agent from response if available, fallback to parameter or current state
        const responseAgent = res.agent;
        const finalAgent = responseAgent || agent || this.state.agent;

        const state: any = {
          usage: res.usage || this.defaultUsage,
          agent: finalAgent,
        };

        this.stateSubject.next(state);
      }),
      catchError((error) => {
        console.log('🐛 DBG history() error:', error);
        const message = error.error?.message || 'Erreur lors du chargement de l\'historique';
        this.notify(message, 'danger', 'exclamation-triangle');
        return of(error);
      })
    );
  }

  chat(prompt: string, options?: {runAgent?: string, ragname?: string, thinking?: boolean, rules?: string[], attachments?: PromptAttachment[], selection?: PromptSelection | null}) {
    const {runAgent, ragname, thinking, rules, attachments, selection} = options || {};
    prompt = prompt.trim();
    const isShort = ['continue','1','2','3','4','5','6','7','8','9','c','help','ci','cc','cm','cs','ok','edgar','help'].indexOf(prompt) > -1;
    if(!isShort && (prompt.length < 3 || typeof prompt !== 'string'  )) {
      return of('');
    }

    //
    // Reject concurrent user queries for now.
    // - The first request flips the service state to "prompt" almost immediately
    // - Any second click/query while the service is starting or streaming is ignored
    const isBusy = this.state.status === 'prompt' || this.state.status === 'running';
    if (isBusy) {
      return of('');
    }

    // ✅ Use new URL structure with agent parameter
    const agentParam = runAgent || 'current';
    const streamUrl = `/api/agent/${agentParam}`;

    // ✅ Add ragname and rules to request body if provided
    const requestBody: any = {query: prompt, agent: runAgent, thinking };
    if (ragname) {
      requestBody.ragname = ragname;
    }
    if (rules?.length) {
      requestBody.rules = rules;
    }
    if (attachments?.length) {
      requestBody.attachments = attachments;
    }
    if (selection?.text?.trim()) {
      requestBody.selection = selection;
    }

    console.log('🐛 DBG chat() requestBody:', requestBody);

    return this.stream(streamUrl, requestBody).pipe(
      tap({
        complete: () => {
          // ✅ Sync with history after streaming completes
          // this.history(false, runAgent).subscribe();
        }
      })
    );
  }

  /**
   * ✅ Add temporary user message to discussion
   */
  private addTemporaryUserMessage(prompt: string, agent: string) {
    const currentDiscussion = this.discussionSubject.value;
    const tempMessage = {
      id: `temp-user-${Date.now()}`,
      role: 'user' as const,
      content: prompt,
      timestamp: new Date()
    };

    // Update discussion with temporary user message
    this.discussionSubject.next({
      ...currentDiscussion,
      messages: [...currentDiscussion.messages, tempMessage],
      updatedAt: new Date()
    });
  }

  /**
   * ✅ Create temporary assistant message at start of streaming
   */
  private createTemporaryAssistantMessage(): string {
    const currentDiscussion = this.discussionSubject.value;
    const messages = [...currentDiscussion.messages];

    const message = messages.find(msg =>
      msg.role === 'assistant' && msg.id.startsWith('temp-assistant-')
    );
    if(message){
      return message.id;
    }

    // Create new temporary assistant message with empty steps
    const tempAssistantMessage = {
      id: `temp-assistant-${Date.now()}`,
      role: 'assistant' as const,
      content: '',
      timestamp: new Date(),
      steps: [] as AssistantStep[]
    };
    messages.push(tempAssistantMessage);

    // Update discussion with new assistant message
    this.discussionSubject.next({
      ...currentDiscussion,
      messages,
      updatedAt: new Date()
    });

    return tempAssistantMessage.id;
  }

  /**
   * ✅ Update existing temporary assistant message (no discussionSubject.next)
   */
  private updateTemporaryAssistantMessage(content: string, steps?: AssistantStep[]) {
    const currentDiscussion = this.discussionSubject.value;
    const messages = currentDiscussion.messages;

    // Find existing temporary assistant message
    const assistantMsgIndex = messages.findIndex(msg =>
      msg.role === 'assistant' && msg.id.startsWith('temp-assistant-')
    );

    if (assistantMsgIndex !== -1) {
      // Update existing temporary assistant message directly (no new object)
      const existingMessage = messages[assistantMsgIndex];
      existingMessage.content = content;
      if (steps) {
        existingMessage.steps = steps;
      }
      // ✅ No discussionSubject.next here - direct mutation for performance
    }
  }

  stream(url: string, body: any, options: { raw?: boolean } = {}) {
    return new Observable<string>(observer$ => {
      // ✅ Create temporary user message and update state
      const agentParam = body.agent || 'current';
      this.addTemporaryUserMessage(body.query, agentParam);

      this.updateState({
        status: 'prompt',
        content: body.query,
        agent: body.agent || this.state.agent,
        thinking: body.thinking || false
      });

      // ✅ Use HttpRequest with reportProgress - MSAL interceptor will add token automatically!
      const headers = new HttpHeaders()
        .set('Accept', 'text/event-stream')
        .set('Cache-Control', 'no-cache')
        .set('ngsw-bypass', 'true');

      const request = new HttpRequest('POST', url, body, {
        reportProgress: true,
        responseType: 'text',
        headers: headers
      });

      let responseText = '';
      let lastIndex = 0;
      let stepsAccum: AssistantStep[] = [];
      let contentAccum = '';
      let messageCreated = false;

      // ✅ Subscribe to HTTP events with automatic token handling
      const subscription = this.$http.request(request).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.DownloadProgress) {
            // ✅ Streaming in progress
            if (!messageCreated) {
              this.createTemporaryAssistantMessage();
              messageCreated = true;
            }

            // Get partial text from event
            const partialText = (event as any).partialText || '';
            if (partialText) {
              responseText = partialText;
              const textChunk = responseText.substring(lastIndex);
              lastIndex = responseText.length;

              // ✅ Parse steps incrementally from this chunk for minimum latency
              const chunkSteps = parseSteps(textChunk);
              if (chunkSteps.length) {
                stepsAccum = _deduplicateSteps([...stepsAccum, ...chunkSteps]);
                this.updateState({
                  status: 'running',
                  steps: stepsAccum,
                  agent: body.agent
                });
              }

              // ✅ Append content incrementally and update temp assistant message
              const cleanedChunk = this.cleanText(textChunk);
              if (cleanedChunk && cleanedChunk.trim()) {
                contentAccum += cleanedChunk;
                this.updateState({
                  status: 'running',
                  content: contentAccum,
                  agent: body.agent
                });
                this.updateTemporaryAssistantMessage(contentAccum, stepsAccum);
              }

              observer$.next(options.raw ? textChunk : cleanedChunk);
            }
          } else if (event.type === HttpEventType.Response) {
            // ✅ Stream completed
            this.updateState({
              status: 'end',
              agent: body.agent || this.state.agent,
              content: undefined,  // ✅ Clear streaming data
              steps: undefined     // ✅ Clear steps data
            });
            observer$.complete();
          }
        },
        error: (err) => {
          console.error('HTTP stream error:', err);
          this.updateState({
            status: 'error',
            error: err.status === 401 ? 'authentication' : 'server',
            agent: body.agent || this.state.agent,
            content: undefined,
            steps: undefined
          });
          observer$.error(err);
        }
      });

      // ✅ Cleanup function
      return () => {
        subscription.unsubscribe();
      };
    });
  }

  async whisper(state): Promise<string> {
    if ((!state?.blob && !state?.chunk) || state.silent) {
      return '';
    }

    const formData = new FormData();
    formData.append('audio', state.blob);
    formData.append('type', state.type);
    formData.append('file','audio.wav');

    // ✅ Ajouter la transcription précédente comme contexte pour Whisper
    if (state.previousText) {
      formData.append('previousText', state.previousText);
    }

    try {
      const resp = await this.$http.post<{transcription: string}>('/api/transcribe', formData, {
        withCredentials: true
      }).toPromise();

      const transcription = resp.transcription || '';
      this.transcriptionsSubject.next(transcription);
      return transcription;
    } catch (error: any) {
      console.error('❌ Erreur transcription:', error);

      // FIXME  rationalize errors with a function
      //
      // Extraire le message d'erreur du serveur
      // - error.error contient la réponse JSON du serveur
      let errorMessage = 'Erreur lors de la transcription audio';
      let errorIcon = 'exclamation-triangle';

      if (error.status === 413) {
        // Erreur 413 - Fichier trop volumineux
        errorMessage = error.error?.message || 'Le fichier audio est trop volumineux';
        const limit = error.error?.limit || '10MB';
        errorMessage += ` (limite: ${limit})`;
        errorIcon = 'exclamation-octagon';
      } else if (error.status === 400) {
        // Erreur 400 - Format non supporté ou autre erreur
        errorMessage = error.error?.error || error.error?.message || 'Format audio non supporté';
      } else if (error.status === 0) {
        // Erreur réseau
        errorMessage = 'Erreur de connexion au serveur';
      } else {
        // Autres erreurs
        errorMessage = error.error?.message || error.message || errorMessage;
      }

      // Afficher la notification à l'utilisateur
      this.notify(errorMessage, 'danger', errorIcon, 10000);

      // Propager l'erreur pour que les composants puissent réagir
      throw new Error(errorMessage);
    }
  }

  abort() {
    this.updateState({
      status: 'end'
    });
  }

  sendFeedback(evaluation: any, message: any) {
    // your logic with $metric
  }

  notify(message:string, variant = 'primary', icon = 'info-circle', duration = 9000) {
    const escapeHtml = (html:string) => {
      const div = document.createElement('div');
      div.textContent = html;
      return div.innerHTML;
    }

    const alert = Object.assign(document.createElement('sl-alert'), {
      variant,
      closable: true,
      duration: duration,
      innerHTML: `
        <sl-icon name="${icon}" slot="icon"></sl-icon>
        ${escapeHtml(message)}
      `
    });

    document.body.append(alert);
    return alert.toast();
  }
}
